export class InternalServerError extends Error {
  constructor() {
    super("An unexpected error occurred.");
  }
}

/**
 * A rule Core refused the request on (422, ApiErrorResponse) that the call maps to no error
 * of its own. It carries Core's reason as its message, so a screen can say why; it is still
 * an InternalServerError for callers that only tell that it failed.
 */
export class DomainRuleError extends InternalServerError {
  readonly code: string;
  /**
   * Every refusal Core listed, each with the field it names when it names one (the addendum
   * values door says which blank). Empty when the refusal came without a list.
   */
  readonly refusals: DomainRefusal[];

  constructor(code: string, reason: string, refusals: DomainRefusal[] = []) {
    super();
    this.name = "DomainRuleError";
    this.code = code;
    this.message = reason;
    this.refusals = refusals;
  }
}

/** One of Core's refusals: `{ code, reason, field? }`. */
export interface DomainRefusal {
  code: string;
  reason: string;
  field?: string;
}

export class NotFoundError extends Error {
  constructor() {
    super("The requested resource was not found.");
  }
}

export class BadRequestError extends Error {
  constructor() {
    super("The request was invalid.");
  }
}

/**
 * A request Core refused as invalid (400) with its reason: a validation message, such as a
 * sixth renewal clause, or the endpoint's own words. It carries that reason as its message,
 * so a screen can say why; it is still a BadRequestError for callers that only tell it was
 * refused.
 */
export class RequestRefusedError extends BadRequestError {
  constructor(reason: string) {
    super();
    this.name = "RequestRefusedError";
    this.message = reason;
  }
}

export class TooManyRequestsError extends Error {
  constructor() {
    super("Too many requests. Please try again later.");
  }
}

export class AgentNotApprovedForPropertyError extends Error {
  constructor() {
    super(
      "The landlord must approve your brokerage before you can invite renters to this property.",
    );
  }
}

export class AgentNotInAgencyError extends Error {
  constructor() {
    super("The target agent is not in the same agency as you.");
  }
}

export class AgentAlreadyLinkedToPropertyError extends Error {
  constructor() {
    super("This agent is already assigned to the property.");
  }
}

export class AgentInvitationExpiredError extends Error {
  constructor() {
    super("This invitation has expired.");
  }
}

export class AgentInvitationAlreadyProcessedError extends Error {
  constructor() {
    super("This invitation has already been processed.");
  }
}

export class AgentInvitationNotFoundError extends Error {
  constructor() {
    super("This invitation link is invalid or no longer exists.");
  }
}

export class LandlordCannotInviteSelfError extends Error {
  constructor() {
    super("You can't invite yourself to manage your own property.");
  }
}

export class CannotUnassignAgencyManagerError extends Error {
  constructor() {
    super("Agency managers cannot be unassigned from a property.");
  }
}

export class OnlyAgencyManagerCanManagePropertyAgentsError extends Error {
  constructor() {
    super(
      "Only the agency manager for this property can assign or unassign agents.",
    );
  }
}

export class PropertyAlreadyExistsError extends Error {
  constructor() {
    super("You have already added a property with the same title deed number.");
  }
}

export class PropertyManagedBySomeoneElseError extends Error {
  constructor() {
    super(
      "This property is managed by another user. Please check the title deed number.",
    );
  }
}

export class PropertyCannotBeDiscardedError extends Error {
  constructor() {
    super(
      "This property cannot be discarded because it has offers that are beyond the draft or deposit payment stage.",
    );
  }
}

export class LandlordHasConfirmedIdentityRequiredError extends Error {
  constructor() {
    super(
      "You must be verified via Persona or (UAE Pass with uploaded Emirates ID) to perform this action.",
    );
  }
}

/** Core's one-contract-per-property lock, as the owner or manager renewing hears it. */
export class ContractAlreadyInProgressError extends Error {
  constructor() {
    super(
      "Another contract is already in progress on this property. Finish or withdraw it before sending renewal terms.",
    );
  }
}

export class InsufficientCreditsError extends Error {
  constructor() {
    super("You don't have enough credits to perform this action.");
  }
}

export class SubscriptionRequiredError extends Error {
  constructor() {
    super("An active subscription is required to perform this action.");
  }
}

export class ScoreAlreadyRevealedError extends Error {
  constructor() {
    super("This score has already been revealed.");
  }
}

export class PaymentMethodRequiredError extends Error {
  constructor() {
    super("Add a payment method to continue.");
  }
}

export class PropertyPaymentFailedError extends Error {
  constructor() {
    super("Property payment failed. Please update your card.");
  }
}

export class IdentityVerificationRequiredError extends Error {
  constructor() {
    super("You must complete identity verification before adding a property.");
  }
}

export class IdentityDocumentNotFoundError extends Error {
  constructor() {
    super("The specified identity document does not exist.");
  }
}

export class NoAccessToIdentityDocumentError extends Error {
  constructor() {
    super("You do not have access to this identity document.");
  }
}

export class LandlordAlreadyAccessibleError extends Error {
  constructor() {
    super(
      "This landlord already exists in your accessible landlords. Please select them from the dropdown instead.",
    );
  }
}

export class SigningVerificationRequiredError extends Error {
  constructor() {
    super("Verify your identity with UAE Pass before signing.");
  }
}

export class SigningIdentityMismatchError extends Error {
  constructor() {
    super("The verified identity does not match this contract's tenant.");
  }
}

export class SigningEmiratesIdMissingError extends Error {
  constructor() {
    super("There is no Emirates ID on file for this contract yet.");
  }
}

export class SigningDocumentsNotAvailableError extends Error {
  constructor() {
    super("This contract is not waiting for documents right now.");
  }
}

export class SigningIdentityDocumentError extends Error {
  constructor() {
    super(
      "We could not read a current Emirates ID. Add clear images of both sides, then submit again.",
    );
  }
}

/** What the signing page says when Core reads the passport sent as expired (TenantPassportExpired). */
export const SIGNING_PASSPORT_EXPIRED_MESSAGE =
  "This passport has expired. Add a current passport, then submit again.";

// Not an ID-card error: the page asks for a new passport and keeps both Emirates ID slots.
export class SigningPassportExpiredError extends Error {
  constructor() {
    super(SIGNING_PASSPORT_EXPIRED_MESSAGE);
  }
}

export class SigningIdentityCardMismatchError extends SigningIdentityDocumentError {
  constructor() {
    super();
    this.message =
      "The two sides are not the same Emirates ID card. Send both sides of one card again.";
  }
}

// A renewal the tenant can no longer accept or decline on their page (Core answers 409): it
// moved on (documents sent, signed, withdrawn) or its seven days ran out since the page looked.
export class SigningRenewalAnswerClosedError extends Error {
  constructor() {
    super("This renewal can no longer be answered here.");
  }
}

// The tenant's Ejari fee link (Core answers 409): the fee is already paid (or paid and being
// confirmed), the contract was cancelled or expired, or nothing is due on it yet.
export class EjariFeeAlreadyPaidError extends Error {
  constructor() {
    super("The Ejari registration fee is already paid.");
  }
}

export class EjariFeeOfferClosedError extends Error {
  constructor() {
    super("This contract was cancelled or expired.");
  }
}

export class EjariFeeNotDueError extends Error {
  constructor() {
    super("There is no Ejari registration fee to pay on this contract yet.");
  }
}

export class ContactAlreadyInUseError extends Error {
  constructor() {
    super(
      "That email already belongs to an account. Use the renter's own contact details.",
    );
  }
}

export class InvalidPhoneNumberError extends Error {
  constructor() {
    super(
      "Enter the mobile in international format, for example +971501234567.",
    );
  }
}

export class OwnerInvitationNotFoundError extends Error {
  constructor() {
    super("This invitation link is invalid or no longer exists.");
  }
}

export class OwnerInvitationExpiredError extends Error {
  constructor() {
    super("This invitation has expired.");
  }
}

// The addendum doors (Core.App/Addenda/AddendumResults.cs), in Core's own words.

/** 503 on Approve: bondoq did not render the PDF, so nothing was stored. */
export class AddendumRendererUnavailableError extends Error {
  constructor() {
    super(
      "We couldn't prepare the addendum's PDF just now. Nothing was approved; try again in a moment.",
    );
  }
}

/** 409 on Approve: a value was saved while the PDF was rendering. */
export class AddendumValuesChangedError extends Error {
  constructor() {
    super(
      "A value changed while approving. Check the values and approve again.",
    );
  }
}

/** 409: a newer draft replaced this one; it takes no values and can't be approved. */
export class AddendumDraftSupersededError extends Error {
  constructor() {
    super(
      "A newer draft has replaced this version. Ask Hazel for the latest link.",
    );
  }
}

/** 409 on a values save: the draft was approved meanwhile, so it takes no values. */
export class AddendumAlreadyApprovedError extends Error {
  constructor() {
    super(
      "This version is approved and can't be changed. Ask Hazel to prepare any changes.",
    );
  }
}

/** The invitation was accepted, or the owner it names has since joined another way. */
export class OwnerInvitationAlreadyAcceptedError extends Error {
  constructor() {
    super("This invitation has already been accepted.");
  }
}

/** A page-safe conflict/refusal with Core's latest-version pointer, when supplied. */
export class ApiResponseRefusalError extends DomainRuleError {
  constructor(
    code: string,
    reason: string,
    public readonly latestDraftId?: string,
  ) {
    super(code, reason);
  }
}
